import type { ReactElement } from 'react';
import { Flex, Image } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { SoftLink } from '../../../components/soft-link/SoftLink';
import { usePDP } from '../../../contexts/pdp/usePDP';
import { Button, Header, Text } from '../../../ui-components';
import { useCustomPreferenceOrDefault } from '../../../utils/api';
import { CustomPreference } from '../../../utils/api/CustomPreference';

export const GiftCard = (): ReactElement | null => {
    const physicalGiftCardImageURL = useCustomPreferenceOrDefault(
        CustomPreference.PHYSICAL_GIFT_CARD_IMAGE_URL,
        '',
    );

    const electronicGiftCardImageURL = useCustomPreferenceOrDefault(
        CustomPreference.ELECTRONIC_GIFT_CARD_IMAGE_URL,
        '',
    );
    const { product } = usePDP();
    const isPhysical = product?.c_gcType === 'physical';

    return (
        <Flex
            flex={1}
            maxWidth="820px"
            order={{
                base: isPhysical ? 2 : 1,
                md: 'initial',
            }}
        >
            <Flex
                direction="column"
                gap={4}
                paddingX={{ base: 4, sm: 8, md: 0 }}
                data-testid={isPhysical ? 'gift-card-physical' : 'gift-card-electronic'}
            >
                <SoftLink
                    to={product?.c_pwaUrl ?? ''}
                    data-testid={`${product?.c_gcType}-gift-card-landing-image`}
                >
                    <Image
                        height={{ base: '240px', md: '300px', lg: '320px', xl: '480px' }}
                        width="100%"
                        src={isPhysical ? physicalGiftCardImageURL : electronicGiftCardImageURL}
                        alt={isPhysical ? 'Physical Gift Card' : 'Electronic Gift Card'}
                        objectFit="cover"
                    />
                </SoftLink>
                <Flex direction="column" gap={6} alignItems="center">
                    <Flex direction="column" gap={1} maxWidth={{ xl: '60%' }} alignItems="center">
                        <SoftLink
                            to={product?.c_pwaUrl ?? ''}
                            data-testid={`${product?.c_gcType}-gift-card-landing-title`}
                        >
                            <Header as="h4" variant="header4" align="center" style="bold">
                                {isPhysical ? (
                                    <FormattedMessage
                                        id="gift_cards.landing_page.physical_git_card.title"
                                        defaultMessage="Physical Gift Cards"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="gift_cards.landing_page.e_git_card.title"
                                        defaultMessage="E-Gift Cards"
                                    />
                                )}
                            </Header>
                        </SoftLink>
                        <Text align="center">
                            {isPhysical ? (
                                <FormattedMessage
                                    id="gift_cards.landing_page.physical_git_card.description"
                                    defaultMessage="The perfect gift to give and get, Sur La Table gift cards can be shipped or picked up at a store near you."
                                />
                            ) : (
                                <FormattedMessage
                                    id="gift_cards.landing_page.e_git_card.description"
                                    defaultMessage="Give a gift that every cook would love to find in their inbox. E-gift cards are just as flexible as our physical gift cards and make the perfect last-minute presents."
                                />
                            )}
                        </Text>
                    </Flex>
                    <Flex justifyContent="center">
                        <SoftLink
                            to={product?.c_pwaUrl ?? ''}
                            data-testid={`${product?.c_gcType}-gift-card-landing-cta`}
                        >
                            <Button
                                variant="primary-black"
                                id={
                                    isPhysical
                                        ? 'gift-card-physical-button'
                                        : 'gift-card-electronic-button'
                                }
                            >
                                {isPhysical ? (
                                    <FormattedMessage
                                        id="gift_cards.landing_page.physical_git_card.button"
                                        defaultMessage="Mail a Gift Card"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="gift_cards.landing_page.e_git_card.button"
                                        defaultMessage="Email a Gift Card"
                                    />
                                )}
                            </Button>
                        </SoftLink>
                    </Flex>
                </Flex>
            </Flex>
        </Flex>
    );
};
